import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { cat_id2lbl } from '../../lib/cat';
import CreatorHeader from '../../components/creator/CreatorHeader';

import xdate from '../../../../../util/date.js';

type VideoCampaign = {
  id: string;
  video_id: string;
  campaign_id: string;
  processing_status: string;
  processed_video_url?: string;
  ts?: {insert: Date, modified: Date};
  brand_name?: string;
  creator_tier?: string;
  published_url?: string;
  published_status?: string;
  views?: {cnt: number};
  campaigns?: {
    id: string;
    name: string;
    category: string;
    budget_info?: {
      currency?: string;
      creator_tiers?: Record<string, {enabled?: boolean, payment?: number}>;
    };
  };
  videos?: {
    id: string;
    title: string;
    thumbnail_url?: string;
    video_url: string;
  };
};

type SocialConnection = {
  id: string;
  platform: string;
  platform_username: string;
  is_active: boolean;
};

export default function MyCampaigns() {
  const navigate = useNavigate();
  const [videoCampaigns, setVideoCampaigns] = useState<VideoCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [connections, set_connections] = useState<SocialConnection[]>([]);
  const [publishing, set_publishing] = useState<string | null>(null);
  const [delete_target, set_delete_target] = useState<VideoCampaign | null>(null);
  const [deleting, set_deleting] = useState(false);
  const [delete_result, set_delete_result] = useState<
    {amount: number; currency: string} | null>(null);

  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchMyCampaigns();
    fetch_connections();
  }, []);

  const fetch_connections = async () => {
    try {
      let res = await back_app.social_connections();
      if (!res.err && Array.isArray(res))
        set_connections(res.filter((c: SocialConnection) => c.is_active));
    } catch (_e) {  }
  };

  const has_connection = (platform: string) =>
    connections.some(c => c.platform == platform);

  const get_connection = (platform: string) =>
    connections.find(c => c.platform == platform);

  const is_published = (vc: VideoCampaign) =>
    !!(vc.published_url || vc.published_status);

  const payout_for = (vc: VideoCampaign) => {
    let tier = vc.creator_tier || 'small';
    let tiers = vc.campaigns?.budget_info?.creator_tiers || {};
    let payment = tiers[tier]?.payment;
    let currency = vc.campaigns?.budget_info?.currency || 'USD';
    return {amount: +(payment || 0) || 0, currency};
  };

  const handle_delete = (vc: VideoCampaign) => {
    if (is_published(vc))
      return;
    set_delete_target(vc);
  };

  const confirm_delete = async () => {
    if (!delete_target)
      return;
    let vc = delete_target;
    let fallback = payout_for(vc);
    set_deleting(true);
    try {
      let res = await back_app.vcamp_delete(vc.id);
      if (res.err) {
        alert('Delete failed: ' + res.err);
        set_deleting(false);
        return;
      }
      setVideoCampaigns(prev => prev.filter(x => x.id != vc.id));
      set_delete_target(null);
      set_delete_result({
        amount: +res.refund_amount || fallback.amount,
        currency: res.currency || fallback.currency,
      });
    } catch (e) {
      console.error('delete error:', e);
      alert('Failed to delete campaign');
    }
    set_deleting(false);
  };

  const handle_publish = async (vc: VideoCampaign, platform: string) => {
    let conn = get_connection(platform);
    if (!conn)
      return navigate('/creator/profile');
    let key = vc.id + '_' + platform;
    set_publishing(key);
    try {
      let res = await back_app.social_publish({
        vcamp_id: vc.id,
        social_id: conn.id,
        destination: 'reels',
        caption: vc.campaigns?.name || '',
      });
      if (res.err)
        alert('Publish failed: ' + res.err);
      else
        alert('Published to ' + platform + ' successfully!');
    } catch (e) {
      console.error('publish error:', e);
      alert('Failed to publish');
    }
    set_publishing(null);
  };

  const fetchMyCampaigns = async () => {
    try {
      const result = await back_app.vcamp_list();

      if (!result.err) {
        const valid_campaigns = (result || []).filter(
          (vc: VideoCampaign) => vc.campaigns && vc.videos
        );
        setVideoCampaigns(valid_campaigns);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      if ((error as Error).message == 'no session') {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  
  const filteredCampaigns = useMemo(() => {
    let filtered = videoCampaigns;

    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(vc => 
        vc.campaigns?.name.toLowerCase().includes(query) ||
        vc.brand_name?.toLowerCase().includes(query) ||
        vc.videos?.title.toLowerCase().includes(query)
      );
    }

    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(vc => vc.processing_status === statusFilter);
    }

    
    filtered = [...filtered].sort((a, b) => 
      (b.ts?.insert?.getTime() || 0) - (a.ts?.insert?.getTime() || 0)
    );

    return filtered;
  }, [videoCampaigns, searchQuery, statusFilter]);

  const handleCampaignClick = (vc: VideoCampaign) => {
    navigate(`/creator/video-processing?id=${vc.id}&video_id=${vc.video_id}&campaign_id=${vc.campaign_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">My Campaigns</h2>
            <p className="text-gray-600 mt-1">
              {filteredCampaigns.length} {filteredCampaigns.length === 1 ? 'campaign' : 'campaigns'}
            </p>
          </div>
          <button
            onClick={() => navigate('/creator/campaigns')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
          >
            + Browse More Campaigns
          </button>
        </div>

        {}
        <div className="bg-white rounded-xl border p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Search by campaign or brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="completed">✅ Completed</option>
              <option value="processing">⏳ Processing</option>
              <option value="analyzing">🤖 Analyzing</option>
              <option value="pending">⏸️ Pending</option>
            </select>
          </div>
        </div>

        {}
        {filteredCampaigns.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <div className="text-6xl mb-4">
              {searchQuery || statusFilter !== 'all' ? '🔍' : '📁'}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No campaigns found' : 'No campaigns yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Join your first campaign to start earning'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <button
                onClick={() => navigate('/creator/campaigns')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Browse Campaigns
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredCampaigns.map((vc) => (
              <div
                key={vc.id}
                onClick={() => handleCampaignClick(vc)}
                className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
              >
                {}
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden" style={{ aspectRatio: '9/16' }}>
                  {vc.videos?.thumbnail_url || vc.videos?.video_url ? (
                    <>
                      {vc.videos.thumbnail_url ? (
                        <img
                          src={vc.videos.thumbnail_url}
                          alt={vc.videos.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={vc.videos.video_url}
                          className="w-full h-full object-cover"
                          muted
                        />
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-white text-5xl">🎬</div>
                    </div>
                  )}

                  {}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-blue-600 ml-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                      </svg>
                    </div>
                  </div>

                  {}
                  <div className="absolute top-3 right-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      vc.processing_status === 'completed' ? 'bg-green-500 text-white' :
                      vc.processing_status === 'processing' ? 'bg-yellow-500 text-white' :
                      vc.processing_status === 'analyzing' ? 'bg-blue-500 text-white' :
                      vc.processing_status === 'failed' ? 'bg-red-500 text-white' :
                      'bg-gray-500 text-white'
                    }`}>
                      {vc.processing_status === 'completed' ? '✓' :
                       vc.processing_status === 'processing' ? '⏳' :
                       vc.processing_status === 'analyzing' ? '🤖' :
                       vc.processing_status}
                    </span>
                  </div>
                </div>

                {}
                <div className="p-4">
                  {}
                  {vc.brand_name && (
                    <p className="text-xs text-gray-500 mb-1">
                      {vc.brand_name}
                    </p>
                  )}

                  {}
                  <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">
                    {vc.campaigns?.name || 'Campaign'}
                  </h3>

                  {}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-1">
                    {vc.videos?.title || 'Video'}
                  </p>

                  {}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    {vc.campaigns?.category && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-semibold">
                        {cat_id2lbl(vc.campaigns.category)}
                      </span>
                    )}
                    <span>
                      {vc.ts?.insert ? xdate.fmt_lbl_short(vc.ts.insert) : ''}
                    </span>
                  </div>

                  {vc.published_status == 'tracking' && (
                    <p className="mt-2 text-xs font-semibold text-blue-700">
                      Views: {(vc.views?.cnt || 0).toLocaleString()} / 1,000
                    </p>
                  )}
                  {vc.published_status == 'approved' && (
                    <p className="mt-2 text-xs font-semibold text-green-700">
                      Approved - paid
                    </p>
                  )}
                  {vc.published_status == 'rejected' && (
                    <p className="mt-2 text-xs font-semibold text-red-700">
                      Under 1,000 views
                    </p>
                  )}

                  {}
                  {!is_published(vc) && (
                    <div className="mt-3 pt-3 border-t flex items-center justify-between gap-2"
                      onClick={e => e.stopPropagation()}>
                      <span className="text-xs text-gray-500">
                        Refundable: {payout_for(vc).currency} {payout_for(vc).amount.toFixed(2)}
                      </span>
                      <button
                        onClick={() => handle_delete(vc)}
                        className="px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  )}

                  {}
                  {vc.processing_status == 'completed' && (
                    <div className="mt-3 pt-3 border-t flex gap-2"
                      onClick={e => e.stopPropagation()}>
                      {has_connection('facebook') ? (
                        <button
                          onClick={() => handle_publish(vc, 'facebook')}
                          disabled={publishing == vc.id + '_facebook'}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {publishing == vc.id + '_facebook'
                            ? 'Publishing...' : 'Facebook'}
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate('/creator/profile')}
                          className="flex-1 px-3 py-2 border border-blue-300 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-50 flex items-center justify-center gap-1"
                        >
                          Connect FB
                        </button>
                      )}
                      {has_connection('instagram') ? (
                        <button
                          onClick={() => handle_publish(vc, 'instagram')}
                          disabled={publishing == vc.id + '_instagram'}
                          className="flex-1 px-3 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-lg text-xs font-semibold hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-1"
                        >
                          {publishing == vc.id + '_instagram'
                            ? 'Publishing...' : 'Instagram'}
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate('/creator/profile')}
                          className="flex-1 px-3 py-2 border border-pink-300 text-pink-600 rounded-lg text-xs font-semibold hover:bg-pink-50 flex items-center justify-center gap-1"
                        >
                          Connect IG
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {}
      {delete_target && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={() => !deleting && set_delete_target(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  Delete this campaign?
                </h3>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">
                    {payout_for(delete_target).currency}{' '}
                    {payout_for(delete_target).amount.toFixed(2)}
                  </span>{' '}
                  will be refunded to the brand.
                </p>
                {delete_target.campaigns?.name && (
                  <p className="text-xs text-gray-500 mt-2">
                    Campaign: {delete_target.campaigns.name}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => set_delete_target(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirm_delete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete & refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {}
      {delete_result && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={() => set_delete_result(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  Campaign deleted
                </h3>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">
                    {delete_result.currency} {delete_result.amount.toFixed(2)}
                  </span>{' '}
                  refunded to the brand.
                </p>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => set_delete_result(null)}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}